import { useCallback, useEffect, useMemo, useRef } from 'react'
import useSWR from 'swr'
import type { ImportSessionUpdatedEvent } from '@/lib/services/websocket'
import { audienceImportSessionsApi } from '@/lib/api'
import type { IdentityValueMapWorkspace } from '@/types/components/audiences/import-session'
import { useOptionalImportWizardStableContext } from '../context/ImportWizardContext'
import { useImportSessionWebsocket } from './useImportSessionWebsocket'
import { getAudienceImportIdentityValueMapKey } from '../lib/cache-keys'

const IDENTITY_VALUE_MAP_REASONS = new Set([
  'session_updated',
  'session_expired',
  'analyze_completed',
  'analyze_failed',
  'preview_refresh_started',
  'context_materials_cleanup_completed',
  'socket_resynced_after_gap',
  'visibility_resynced_after_gap',
  'socket_subscribed_sync',
])

export function useIdentityValueMapWorkspace(
  sessionId: string,
  canonicalId: string | null,
  options?: {
    enabled?: boolean
    /** Server-selected source column for this value map; required when enabled. */
    valueMapSourceKey?: string | null
    lastMutationRequestId?: string | null
    subscribeToUpdates?: boolean
  },
) {
  const ctx = useOptionalImportWizardStableContext()
  const valueMapSourceKey = options?.valueMapSourceKey ?? null
  const key = useMemo(
    () =>
      options?.enabled === false ||
      valueMapSourceKey == null ||
      !String(valueMapSourceKey).trim()
        ? null
        : getAudienceImportIdentityValueMapKey(
            sessionId,
            canonicalId,
            valueMapSourceKey,
          ),
    [canonicalId, options?.enabled, sessionId, valueMapSourceKey],
  )

  const valueMapQuery = useSWR<IdentityValueMapWorkspace>(
    key,
    (_key: unknown, fetcherOptions?: { signal?: AbortSignal }) =>
      audienceImportSessionsApi.getIdentityValueMapWorkspace(
        sessionId,
        canonicalId!,
        fetcherOptions?.signal,
      ),
    {
      revalidateOnFocus: false,
      dedupingInterval: 250,
      keepPreviousData: false,
    },
  )

  const revalidateValueMap = useCallback(async () => {
    await valueMapQuery.mutate()
  }, [valueMapQuery])

  const shouldSubscribeToUpdates = options?.subscribeToUpdates !== false
  const insideWizardWithSubscribe = ctx !== null && shouldSubscribeToUpdates
  const sessionIdForWebsocket = insideWizardWithSubscribe
    ? ''
    : shouldSubscribeToUpdates
      ? sessionId
      : ''

  const handlerRef = useRef<(event: ImportSessionUpdatedEvent) => void>(() => {})
  handlerRef.current = (event: ImportSessionUpdatedEvent) => {
    if (!canonicalId || !IDENTITY_VALUE_MAP_REASONS.has(event.reason)) {
      return
    }
    void revalidateValueMap()
  }
  const stableHandler = useCallback((event: ImportSessionUpdatedEvent) => {
    handlerRef.current(event)
  }, [])

  useEffect(() => {
    if (!ctx || !shouldSubscribeToUpdates) return
    return ctx.registerImportSessionEventHandler(stableHandler)
  }, [ctx, shouldSubscribeToUpdates, stableHandler])

  useImportSessionWebsocket(sessionIdForWebsocket, stableHandler, {
    lastMutationRequestId: options?.lastMutationRequestId ?? null,
  })

  return {
    valueMapWorkspace: valueMapQuery.data ?? null,
    error: valueMapQuery.error as Error | undefined,
    isLoading: valueMapQuery.isLoading,
    isValidating: valueMapQuery.isValidating,
    mutate: valueMapQuery.mutate,
  }
}
